import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Filter, Search } from "lucide-react";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { getStatusLabel } from "@/utils/statusMapper";
import {
  listApiCallTypes,
  listApiCallStatuses,
} from "@/services/apiCallsService";
import {
  getAccessibleClients,
  type AccessibleClient,
} from "@/services/clientsService";
import { listProducts } from "@/services/productsService";
import { useToast } from "@/hooks/use-toast";
import type { DashboardUnifiedFilters } from "@/types/dashboardFilters";
import { createDefaultDashboardFilters } from "@/types/dashboardFilters";

interface DashboardUnifiedFiltersProps {
  value: DashboardUnifiedFilters;
  onApply: (filters: DashboardUnifiedFilters) => void;
}

type PeriodPreset =
  | "today"
  | "this_week"
  | "this_month"
  | "last_7"
  | "last_30"
  | "custom";

function resolvePreset(start: Date, end: Date): PeriodPreset {
  const now = new Date();
  const todayStart = startOfDay(now).getTime();
  const todayEnd = endOfDay(now).getTime();
  if (
    startOfDay(start).getTime() === todayStart &&
    endOfDay(end).getTime() === todayEnd
  ) {
    return "today";
  }

  const weekStart = startOfWeek(now, { weekStartsOn: 1 }).getTime();
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 }).getTime();
  if (
    startOfDay(start).getTime() === weekStart &&
    endOfDay(end).getTime() === weekEnd
  ) {
    return "this_week";
  }

  const monthStart = startOfMonth(now).getTime();
  const monthEnd = endOfMonth(now).getTime();
  if (
    startOfDay(start).getTime() === monthStart &&
    endOfDay(end).getTime() === monthEnd
  ) {
    return "this_month";
  }

  const last7Start = startOfDay(subDays(now, 6)).getTime();
  if (
    startOfDay(start).getTime() === last7Start &&
    endOfDay(end).getTime() === todayEnd
  ) {
    return "last_7";
  }

  const last30Start = startOfDay(subDays(now, 29)).getTime();
  if (
    startOfDay(start).getTime() === last30Start &&
    endOfDay(end).getTime() === todayEnd
  ) {
    return "last_30";
  }

  return "custom";
}

function rangeFromPreset(preset: PeriodPreset): { start: Date; end: Date } {
  const now = new Date();
  switch (preset) {
    case "today":
      return { start: startOfDay(now), end: endOfDay(now) };
    case "this_week":
      return {
        start: startOfWeek(now, { weekStartsOn: 1 }),
        end: endOfWeek(now, { weekStartsOn: 1 }),
      };
    case "this_month":
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case "last_7":
      return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
    case "last_30":
      return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
    default:
      return { start: startOfMonth(now), end: endOfMonth(now) };
  }
}

export function DashboardUnifiedFiltersCard({
  value,
  onApply,
}: DashboardUnifiedFiltersProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<DashboardUnifiedFilters>(value);
  const [clients, setClients] = useState<AccessibleClient[]>([]);
  const [products, setProducts] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [callTypes, setCallTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [callStatuses, setCallStatuses] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<PeriodPreset>(
    resolvePreset(value.startDate, value.endDate),
  );
  const [picking, setPicking] = useState<"start" | "end">("start");

  useEffect(() => {
    setDraft(value);
    setActivePreset(resolvePreset(value.startDate, value.endDate));
  }, [value]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [clientsRes, typesRes, statusesRes, productsRes] =
          await Promise.all([
            getAccessibleClients(),
            listApiCallTypes(),
            listApiCallStatuses(),
            listProducts({ limit: 200 }),
          ]);

        if (clientsRes.success && clientsRes.data) {
          setClients(clientsRes.data.clients);
        }
        if (typesRes.success && typesRes.data) {
          setCallTypes(typesRes.data);
        }
        if (statusesRes.success && statusesRes.data) {
          setCallStatuses(statusesRes.data);
        }
        if (productsRes.success && productsRes.data?.products) {
          setProducts(
            productsRes.data.products.map((p) => ({
              id: p.id,
              name: p.name,
            })),
          );
        }
      } catch {
        toast({
          title: "Erro",
          description: "Não foi possível carregar opções dos filtros",
          variant: "destructive",
        });
      }
    };

    void loadMeta();
  }, [toast]);

  const applyPreset = (preset: PeriodPreset) => {
    if (preset === "custom") {
      setActivePreset("custom");
      setPicking("start");
      return;
    }
    const { start, end } = rangeFromPreset(preset);
    setActivePreset(preset);
    setDraft((prev) => ({ ...prev, startDate: start, endDate: end }));
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (!date) return;
    setActivePreset("custom");
    if (picking === "start") {
      setDraft((prev) => {
        const end =
          prev.endDate && prev.endDate < date ? endOfDay(date) : prev.endDate;
        return { ...prev, startDate: startOfDay(date), endDate: end };
      });
      setPicking("end");
      return;
    }
    setDraft((prev) => ({
      ...prev,
      endDate: endOfDay(date),
      startDate:
        prev.startDate && prev.startDate > date
          ? startOfDay(date)
          : prev.startDate,
    }));
  };

  const handleApply = () => {
    const start = draft.startDate;
    const end = draft.endDate;
    if (start > end) {
      toast({
        title: "Período inválido",
        description: "A data inicial não pode ser posterior à data final",
        variant: "destructive",
      });
      return;
    }
    onApply({
      ...draft,
      startDate: startOfDay(start),
      endDate: endOfDay(end),
    });
    setPeriodOpen(false);
  };

  const handleClear = () => {
    const defaults = createDefaultDashboardFilters();
    setDraft(defaults);
    setActivePreset("this_month");
    onApply(defaults);
  };

  const periodLabel = `${format(draft.startDate, "dd/MM/yyyy", {
    locale: ptBR,
  })} – ${format(draft.endDate, "dd/MM/yyyy", { locale: ptBR })}`;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Filter className="h-5 w-5 text-primary" />
          Filtros
        </CardTitle>
        <CardDescription>
          Filtro único do dashboard — aplicado a tabelas, indicadores e
          histórico
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div className="space-y-2">
            <Label htmlFor="dashboard-search">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="dashboard-search"
                placeholder="Buscar..."
                value={draft.search}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, search: e.target.value }))
                }
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cliente</Label>
            <Select
              value={draft.client}
              onValueChange={(client) =>
                setDraft((prev) => ({ ...prev, client }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                <SelectItem value="sandbox">Sandbox</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.company || client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select
              value={draft.type}
              onValueChange={(type) => setDraft((prev) => ({ ...prev, type }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {callTypes.map((type) => (
                  <SelectItem key={type.id} value={type.name}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={draft.status}
              onValueChange={(status) =>
                setDraft((prev) => ({ ...prev, status }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {callStatuses.map((status) => (
                  <SelectItem key={status.id} value={status.name}>
                    {getStatusLabel(status.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Especialista</Label>
            <Select
              value={draft.product}
              onValueChange={(product) =>
                setDraft((prev) => ({ ...prev, product }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os especialistas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os especialistas</SelectItem>
                {products.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Período</Label>
            <Popover open={periodOpen} onOpenChange={setPeriodOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !draft.startDate && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  <span className="truncate">{periodLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 overflow-hidden border-primary/10 shadow-lg" align="end">
                <div className="flex flex-col sm:flex-row">
                  <div className="border-b sm:border-b-0 sm:border-r border-primary/10 bg-primary/5 p-3 min-w-[168px] space-y-1">
                    <p className="px-3 pb-2 text-[11px] font-medium uppercase tracking-wide text-primary/70">
                      Atalhos
                    </p>
                    {(
                      [
                        ["today", "Hoje"],
                        ["this_week", "Esta semana"],
                        ["this_month", "Este mês"],
                        ["last_7", "Últimos 7 dias"],
                        ["last_30", "Últimos 30 dias"],
                        ["custom", "Personalizado"],
                      ] as Array<[PeriodPreset, string]>
                    ).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => applyPreset(key)}
                        className={cn(
                          "w-full rounded-md px-3 py-2 text-left text-sm transition-colors",
                          activePreset === key
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-foreground/80 hover:bg-primary/10 hover:text-primary",
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="p-3 space-y-3 bg-background">
                    <div className="flex gap-2 text-xs">
                      <button
                        type="button"
                        className={cn(
                          "rounded-full px-3 py-1.5 border transition-colors",
                          picking === "start"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:border-primary/40",
                        )}
                        onClick={() => setPicking("start")}
                      >
                        Início: {format(draft.startDate, "dd/MM")}
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "rounded-full px-3 py-1.5 border transition-colors",
                          picking === "end"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:border-primary/40",
                        )}
                        onClick={() => setPicking("end")}
                      >
                        Fim: {format(draft.endDate, "dd/MM")}
                      </button>
                    </div>
                    <Calendar
                      mode="range"
                      selected={{
                        from: draft.startDate,
                        to: draft.endDate,
                      }}
                      onSelect={handleCalendarSelect}
                      className="p-0 pointer-events-auto"
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={handleApply}>Aplicar Filtros</Button>
          <Button variant="outline" onClick={handleClear}>
            Limpar Filtros
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
